import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart
} from 'recharts';
import { useCurrency } from '../contexts/CurrencyContext';
import { buildChartRows, dataSpan, nextAutoWidenPeriod, yDomain } from '../lib/priceChartData';

const SOURCE_LABELS = {
  market: 'Market Price',
  shopify_active: 'Retail listing average',
  shopify_sold: 'Retail sold average',
  ebay_active: 'eBay listing average',
  ebay_sold: 'eBay ended listings average',
  tcgplayer: 'TCG Player'
};

const SOURCE_COLORS = {
  market: '#00E5A0',
  shopify_active: '#4F46E5',
  shopify_sold: '#7C3AED',
  ebay_active: '#F59E0B',
  ebay_sold: '#F97316',
  tcgplayer: '#10B981'
};

const buildPricingContext = (bucket, finish, gradeBucket, periodDays = null) => {
  const normalizedBucket = bucket === 'graded' ? 'graded' : 'raw';
  return {
    bucket: normalizedBucket,
    finish: normalizedBucket === 'raw' ? (finish || 'general') : null,
    grade_bucket: normalizedBucket === 'graded' ? (gradeBucket || 'grade_9') : null,
    period_days: Number.isFinite(Number(periodDays)) ? Number(periodDays) : null,
  };
};

const isInitialDataCompatible = (data, bucket, finish, gradeBucket) => {
  if (!data) return false;

  const expected = buildPricingContext(bucket, finish, gradeBucket);
  const context = data?.__pricing_context;

  if (context && typeof context === 'object') {
    const normalized = buildPricingContext(
      context.bucket,
      context.finish,
      context.grade_bucket
    );
    return normalized.bucket === expected.bucket
      && normalized.finish === expected.finish
      && normalized.grade_bucket === expected.grade_bucket;
  }

  // Legacy bootstrap payloads are always raw/general.
  return expected.bucket === 'raw' && expected.finish === 'general';
};

const PriceChart = ({ 
  cardId, 
  cardName, 
  onDoubleClick, 
  initialData = null,
  bucket = 'raw',
  finish = 'general',
  gradeBucket = null,
  source = 'market',
  sources = null,
  onDataLoaded = null,
  onLoadingChange = null,
  defaultPeriod = 30,  // WO-21: game card pages open on 1Y; every existing caller keeps 30
  edition = null       // WO-21: one edition per series (e.g. Yu-Gi-Oh 1st Edition vs Unlimited); null = all
}) => {
  const [seriesData, setSeriesData] = useState(() => {
    if (
      initialData
      && initialData.data_points
      && initialData.data_points.length > 0
      && isInitialDataCompatible(initialData, bucket, finish, gradeBucket)
    ) {
      const initialSource = initialData.source || 'market';
      return { [initialSource]: initialData };
    }
    return {};
  });
  const [loading, setLoading] = useState(!initialData);
  const [error, setError] = useState(null);
  // Initialize period from initialData if available, otherwise default to 30 days
  const [selectedPeriod, setSelectedPeriod] = useState(() => {
    if (initialData && initialData.period_days) {
      return initialData.period_days;
    }
    return defaultPeriod || 30; // Default to 1 month (more reasonable)
  });
  const apiUrl = process.env.REACT_APP_API_URL || '';
  const { currency, convertCadAmount, formatCurrencyAmount } = useCurrency();
  // WO-35: until the shopper picks a range, an opening range with fewer than two prices steps up to 1Y, then ALL
  // (Pokemon pages open on 1M, where the market series often has a single point).
  const userPickedPeriodRef = useRef(false);

  const periodOptions = [
    { label: '1M', days: 30 },
    { label: '3M', days: 90 },
    { label: '6M', days: 180 },
    { label: '1Y', days: 365 },
    { label: '2Y', days: 730 },
    { label: 'ALL', days: 3650 }
  ];

  const effectiveSources = useMemo(() => {
    const base = Array.isArray(sources) && sources.length > 0 ? sources : [source || 'market'];
    const seen = new Set();
    const unique = [];
    base.forEach((item) => {
      const key = (item || '').trim();
      if (!key || seen.has(key)) return;
      seen.add(key);
      unique.push(key);
    });
    return unique.length ? unique : ['market'];
  }, [sources, source]);

  const primarySource = effectiveSources[0] || 'market';

  useEffect(() => {
    if (!cardId) return;

    const abortController = new AbortController();
    const signal = abortController.signal;

    const initialSeries = {};
    if (
      initialData
      && initialData.data_points
      && initialData.data_points.length > 0
      && isInitialDataCompatible(initialData, bucket, finish, gradeBucket)
    ) {
      const initialPeriod = initialData.period_days || 30;
      const initialSource = initialData.source || primarySource;
      if ((initialPeriod === selectedPeriod || (selectedPeriod === 30 && initialPeriod === 30))
        && effectiveSources.includes(initialSource)) {
        initialSeries[initialSource] = initialData;
      }
    }

    const sourcesToFetch = effectiveSources.filter((src) => !initialSeries[src]);
    const availableSources = new Set(initialData?.available_sources || []);

    const finalizeLoad = (dataBySource) => {
      const wider = userPickedPeriodRef.current ? null : nextAutoWidenPeriod(selectedPeriod, dataBySource);
      if (wider) {
        setSelectedPeriod(wider);   // the effect runs again for the wider range; keep the spinner
        return;
      }
      setSeriesData(dataBySource);
      setLoading(false);
      if (onLoadingChange) {
        onLoadingChange(false);
      }
      let primaryData = dataBySource[primarySource]
        || Object.values(dataBySource).find((entry) => entry && entry.data_points && entry.data_points.length > 0);
      if (!primaryData) {
        primaryData = dataBySource[primarySource] || Object.values(dataBySource).find((entry) => entry);
      }
      if (onDataLoaded && primaryData) {
        const resolvedSource = primaryData.source || primarySource;
        onDataLoaded({
          ...primaryData,
          available_sources: Array.from(availableSources),
          source: resolvedSource,
          __pricing_context: buildPricingContext(bucket, finish, gradeBucket, selectedPeriod),
        });
      }
    };

    if (sourcesToFetch.length === 0) {
      finalizeLoad(initialSeries);
      return () => {
        abortController.abort();
      };
    }

    const fetchSource = async (src) => {
      const params = new URLSearchParams();
      params.set('days', selectedPeriod);
      params.set('bucket', bucket);
      params.set('source', src);
      if (bucket === 'raw') {
        params.set('finish', finish || 'general');
        if (edition) params.set('edition', edition);
      } else if (bucket === 'graded') {
        params.set('grade_bucket', gradeBucket || 'grade_9');
      }   // WO-35: sealed series have no grade bucket (the old code sent grade_9)

      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Request timeout')), 10000);
      });

      const fetchPromise = fetch(`${apiUrl}/api/pricing/${cardId}/history?${params.toString()}`, { signal });
      const response = await Promise.race([fetchPromise, timeoutPromise]);
      if (signal.aborted) return { source: src, data: null };

      if (!response.ok) {
        if (response.status === 404 || response.status === 500) {
          return { source: src, data: null };
        }
        throw new Error(`Failed to fetch price data: ${response.status}`);
      }

      const result = await response.json();
      if (signal.aborted) return { source: src, data: null };

      if (result.success && result.data) {
        if (Array.isArray(result.data.available_sources)) {
          result.data.available_sources.forEach((item) => availableSources.add(item));
        }
        return { source: src, data: result.data };
      }
      return { source: src, data: null };
    };

    const fetchAll = async () => {
      setLoading(true);
      if (onLoadingChange) {
        onLoadingChange(true);
      }
      setError(null);

      try {
        const results = await Promise.all(sourcesToFetch.map((src) => fetchSource(src)));
        if (signal.aborted) return;
        const combined = { ...initialSeries };
        results.forEach(({ source: src, data }) => {
          combined[src] = data;
        });
        finalizeLoad(combined);
      } catch (err) {
        if (err.name === 'AbortError') return;
        if (!signal.aborted) {
          setError(null);
        }
        finalizeLoad(initialSeries);
      }
    };

    fetchAll();

    return () => {
      abortController.abort();
    };
  }, [cardId, selectedPeriod, apiUrl, initialData, bucket, finish, edition, gradeBucket, effectiveSources, primarySource, onDataLoaded, onLoadingChange]);

  const formatPriceLabel = (value) => {
    return formatCurrencyAmount(value, currency, { fallback: 'N/A' });
  };

  const formatPercent = (value) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return 'N/A';
    return new Intl.NumberFormat('en-CA', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numeric);
  };

  const normalizeNumber = useCallback((value) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
  }, []);

  const getPointPrice = useCallback((point) => {
    const candidates = [
      normalizeNumber(point.avg_price),
      normalizeNumber(point.median_price),
      normalizeNumber(point.min_price),
      normalizeNumber(point.max_price)
    ];
    return candidates.find((value) => value !== null) ?? null;
  }, [normalizeNumber]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
    });
  };

  const getSourceLabel = (key) => SOURCE_LABELS[key] || key;

  // Convert CAD prices before chart scale/range math runs.
  const convertedSeriesData = useMemo(() => {
    const output = {};
    Object.entries(seriesData).forEach(([sourceKey, data]) => {
      if (!data) {
        output[sourceKey] = data;
        return;
      }

      const convertedPoints = (data.data_points || []).map((point) => ({
        ...point,
        avg_price: convertCadAmount(point.avg_price, currency),
        median_price: convertCadAmount(point.median_price, currency),
        min_price: convertCadAmount(point.min_price, currency),
        max_price: convertCadAmount(point.max_price, currency),
      }));

      const summary = data.summary
        ? {
            ...data.summary,
            latest_price: convertCadAmount(data.summary.latest_price, currency),
            highest_price: convertCadAmount(data.summary.highest_price, currency),
            lowest_price: convertCadAmount(data.summary.lowest_price, currency),
            price_change: convertCadAmount(data.summary.price_change, currency),
          }
        : data.summary;

      output[sourceKey] = {
        ...data,
        data_points: convertedPoints,
        summary,
      };
    });
    return output;
  }, [convertCadAmount, currency, seriesData]);

  // Custom tooltip component
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) return null;
    const data = payload[0]?.payload || {};
    return (
      <div className="bg-dark border border-purple/50 rounded-md p-3 text-xs z-10 min-w-56 shadow-lg">
        <div className="text-cream font-medium mb-2">
          {formatDate(label)}
        </div>
        <div className="space-y-2 text-cream/80">
          {payload
            .filter((item) => item && item.value !== undefined && item.value !== null)
            .map((item) => {
              const dataKey = String(item.dataKey || '');
              const sourceKey = dataKey.replace(/^price_/, '').replace(/__\d+$/, '');
              const listingCount = data[`listing_count_${sourceKey}`];
              const minPrice = normalizeNumber(data[`min_price_${sourceKey}`]);
              const maxPrice = normalizeNumber(data[`max_price_${sourceKey}`]);
              return (
                <div key={dataKey} className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full" style={{ backgroundColor: item.color || item.stroke }} />
                    <span className="text-cream">{getSourceLabel(sourceKey)}</span>
                  </div>
                  <div>Price: <span className="font-medium">{formatPriceLabel(item.value)}</span></div>
                  {listingCount !== undefined && (
                    <div>Listings: <span className="text-cream">{listingCount}</span></div>
                  )}
                  {minPrice !== null && maxPrice !== null && (
                    <div>Range: <span className="text-cream/60">{formatPriceLabel(minPrice)} - {formatPriceLabel(maxPrice)}</span></div>
                  )}
                </div>
              );
            })}
        </div>
      </div>
    );
  };

  const activeSeries = useMemo(() => {
    return effectiveSources.filter((src) => convertedSeriesData[src]?.data_points && convertedSeriesData[src].data_points.length > 0);
  }, [convertedSeriesData, effectiveSources]);

  const activeRawSeries = useMemo(() => {
    return effectiveSources.filter((src) => seriesData[src]?.data_points && seriesData[src].data_points.length > 0);
  }, [effectiveSources, seriesData]);

  const primaryData = convertedSeriesData[primarySource] || (activeSeries.length ? convertedSeriesData[activeSeries[0]] : null);
  const rawPrimaryDataForModal = useMemo(() => {
    const rawPrimary = seriesData[primarySource] || (activeRawSeries.length ? seriesData[activeRawSeries[0]] : null);
    if (!rawPrimary) return null;

    const resolvedSource = rawPrimary.source || primarySource;
    const availableSources = Array.isArray(rawPrimary.available_sources) && rawPrimary.available_sources.length > 0
      ? rawPrimary.available_sources
      : activeRawSeries;

    return {
      ...rawPrimary,
      source: resolvedSource,
      available_sources: availableSources,
      __pricing_context: buildPricingContext(bucket, finish, gradeBucket, selectedPeriod),
    };
  }, [activeRawSeries, bucket, finish, gradeBucket, primarySource, selectedPeriod, seriesData]);

  // WO-35: rows on a real time axis, each source split at gaps longer than GAP_DAYS (no line across missing data).
  const chart = useMemo(
    () => buildChartRows(convertedSeriesData, activeSeries, getPointPrice),
    [activeSeries, convertedSeriesData, getPointPrice]
  );
  const chartDataFormatted = chart.rows;
  const span = dataSpan(chartDataFormatted);

  const primaryKey = `price_${primarySource}`;
  const primaryPrices = chartDataFormatted
    .map((point) => point[primaryKey])
    .filter((value) => Number.isFinite(value));

  const latestPrice = primaryData?.summary?.latest_price ?? (primaryPrices.length ? primaryPrices[primaryPrices.length - 1] : null);
  const highestPrice = primaryData?.summary?.highest_price ?? (primaryPrices.length ? Math.max(...primaryPrices) : null);
  const lowestPrice = primaryData?.summary?.lowest_price ?? (primaryPrices.length ? Math.min(...primaryPrices) : null);
  const priceChangeValue = normalizeNumber(primaryData?.summary?.price_change);
  const priceChangePercentValue = normalizeNumber(primaryData?.summary?.price_change_percent);

  const getModernChart = () => {
    if (chartDataFormatted.length === 0) {
      return (
        <div className="flex items-center justify-center h-64 text-cream/60">
          No price data available
        </div>
      );
    }

    const prices = chartDataFormatted
      .flatMap((point) => activeSeries.map((src) => point[`price_${src}`]))
      .filter((value) => value !== null && value !== undefined);
    const fewPoints = chartDataFormatted.length <= 12;
    
    if (prices.length === 0) {
      return (
        <div className="flex items-center justify-center h-64 text-cream/60">
          No valid price data
        </div>
      );
    }

    return (
      <ResponsiveContainer width="100%" height={280}>
        <AreaChart
          data={chartDataFormatted}
          margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
        >
          <defs>
            {activeSeries.map((src) => {
              const color = SOURCE_COLORS[src] || '#00E5A0';
              return (
                <linearGradient key={src} id={`priceGradient-${src}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={color} stopOpacity={0.25}/>
                  <stop offset="95%" stopColor={color} stopOpacity={0.05}/>
                </linearGradient>
              );
            })}
            <filter id="glow">
              <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
              <feMerge>
                <feMergeNode in="coloredBlur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
          </defs>
          <CartesianGrid 
            strokeDasharray="3 3" 
            stroke="#6A66A3" 
            opacity={0.2}
            vertical={false}
          />
          <XAxis 
            dataKey="ts"
            type="number"
            scale="time"
            domain={['dataMin', 'dataMax']}
            tickFormatter={(ts) => formatDate(ts)}
            stroke="#6A66A3"
            opacity={0.6}
            tick={{ fill: '#E5E5E5', fontSize: 11 }}
            axisLine={{ stroke: '#6A66A3', opacity: 0.3 }}
          />
          <YAxis 
            tickFormatter={formatPriceLabel}
            stroke="#6A66A3"
            opacity={0.6}
            tick={{ fill: '#E5E5E5', fontSize: 11, opacity: 0.5 }}
            axisLine={{ stroke: '#6A66A3', opacity: 0.3 }}
            domain={yDomain(prices)}
          />
          <Tooltip content={<CustomTooltip />} />
          {chart.segments.map(({ src, key, count }) => {
            const color = SOURCE_COLORS[src] || '#00E5A0';
            return (
              <Area
                key={key}
                type="monotone"
                dataKey={key}
                name={src}
                connectNulls
                isAnimationActive={false}
                stroke={color}
                strokeWidth={2}
                fill={`url(#priceGradient-${src})`}
                dot={fewPoints || count <= 2 ? { r: 3, fill: color, stroke: color } : false}
                activeDot={{ 
                  r: 5, 
                  fill: color,
                  stroke: '#1A1A20',
                  strokeWidth: 2,
                  filter: 'url(#glow)'
                }}
              />
            );
          })}
        </AreaChart>
      </ResponsiveContainer>
    );
  };

  return (
    <div className="mt-4">
      <div className="bg-dark rounded-md border border-purple/20 p-4">
        {/* Header */}
        <div className="mb-4">
          <h3 className="text-sm font-medium text-cream">Price History</h3>
        </div>

        {/* Period selector */}
        <div className="flex justify-between mb-4">
          {/* WO-35: never disabled. The old rule compared each range with the span of the range already loaded, so
              opening on 1M with one price disabled every longer range on every page. */}
          {periodOptions.map((period) => {
            return (
              <button
                key={period.days}
                onClick={() => {
                  userPickedPeriodRef.current = true;
                  setSelectedPeriod(period.days);
                }}
                className={`px-2 py-1 text-xs rounded transition ${
                  selectedPeriod === period.days
                    ? 'bg-purple text-cream'
                    : 'text-cream/60 hover:text-cream hover:bg-purple/20'
                }`}
                aria-pressed={selectedPeriod === period.days}
                data-testid={`range-${period.label}`}
                type="button"
              >
                {period.label}
              </button>
            );
          })}
        </div>

        {/* Chart content */}
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple"></div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-32 text-red-400 text-sm">
            Error: {error}
          </div>
        ) : (
          <>
            <div 
              onDoubleClick={onDoubleClick ? () => onDoubleClick(rawPrimaryDataForModal) : undefined}
              className={onDoubleClick ? "cursor-pointer" : ""}
              title={onDoubleClick ? "Double-click to enlarge" : ""}
            >
              {getModernChart()}
            </div>
            {span && (
              <p className="mt-2 text-[11px] text-cream/40" data-testid="chart-span">
                Prices from {formatDate(span.first)} to {formatDate(span.last)}
                {chart.hasGap ? '. A break in a line means no prices were recorded then.' : ''}
              </p>
            )}
            
            {/* Enhanced summary stats */}
            {primaryData && primaryData.summary && (
              <div className="mt-4 grid grid-cols-2 gap-4 text-xs">
                <div>
                  <div className="text-cream/60">Latest Price</div>
                  <div className="text-green font-medium">
                    {formatPriceLabel(latestPrice)}
                  </div>
                </div>
                <div>
                  <div className="text-cream/60">Price Change</div>
                  {priceChangeValue === null || priceChangePercentValue === null ? (
                    <div className="font-medium text-cream/60">N/A</div>
                  ) : (
                    <div className={`font-medium ${
                      priceChangeValue >= 0 ? 'text-green' : 'text-red-400'
                    }`}>
                      {priceChangeValue >= 0 ? '+' : ''}
                      {formatPriceLabel(priceChangeValue)} 
                      ({priceChangePercentValue >= 0 ? '+' : ''}
                      {formatPercent(priceChangePercentValue)}%)
                    </div>
                  )}
                </div>
                <div>
                  <div className="text-cream/60">High</div>
                  <div className="text-cream">
                    {formatPriceLabel(highestPrice)}
                  </div>
                </div>
                <div>
                  <div className="text-cream/60">Low</div>
                  <div className="text-cream">
                    {formatPriceLabel(lowestPrice)}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PriceChart;
